import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROTAS } from '@/app/rotas';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  configuracoesApi,
  type DadosEmpresa,
} from '@/features/configuracoes/api/configuracoesApi';
import { useConfiguracao } from '@/features/configuracoes/hooks/useConfiguracao';
import { PrintPortal } from '@/shared/print/PrintPortal';
import { imprimirComClasse, medirElementoComoPageSize } from '@/shared/print/imprimir';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { Modal } from '@/shared/ui/Modal';
import { IconX } from '@/shared/ui/Icon';
import { pdvApi, type CaixaResumo, type VendaDetalhe } from '../api/pdvApi';
import { CupomVenda58mm } from '../components/CupomVenda58mm';
import { PdvAberturaCaixa } from './PdvAberturaCaixa';
import { PdvDevolucao } from './PdvDevolucao';
import { PdvFechamentoCaixa } from './PdvFechamentoCaixa';
import { PdvFiado } from './PdvFiado';
import { PdvHistoricoVendas } from './PdvHistoricoVendas';
import { PdvRelatorio } from './PdvRelatorio';
import { PdvVender } from './PdvVender';
import { formatarDinheiro, textoDoErro } from '../formato';

type Aba = 'vender' | 'caixa' | 'vendas' | 'devolucao' | 'relatorio' | 'fiado';

const CHAVE_CAIXA = 'oficinaagil.pdv.caixa';

function trocoDaVenda(venda: VendaDetalhe): number {
  return venda.pagamentos.reduce((acc, p) => acc + (p.troco ?? 0), 0);
}

function abaDaUrl(caminho: string): Aba {
  if (caminho.startsWith(ROTAS.pdvCaixa)) return 'caixa';
  if (caminho.startsWith(ROTAS.pdvDevolucao)) return 'devolucao';
  if (caminho.startsWith(ROTAS.pdvRelatorio)) return 'relatorio';
  if (caminho.startsWith(ROTAS.pdvFiado)) return 'fiado';
  if (caminho.startsWith(ROTAS.pdvVendas)) return 'vendas';
  return 'vender';
}

/**
 * PDV em modo tela cheia.
 *
 * O item na barra lateral é a porta; lá dentro a barra some. Quem está no
 * balcão não deve ver doze opções nem precisar do mouse — a tela inteira
 * é o campo de código, o carrinho e o total.
 *
 * O caixa aberto fica guardado no navegador só como conveniência de
 * sessão: a verdade é sempre o servidor/SQLite, consultado no primeiro
 * render.
 */
export function PdvPage() {
  const navegar = useNavigate();
  const { pathname } = useLocation();
  const aba = abaDaUrl(pathname);
  const dialogos = useDialogos();

  const { estado } = useAuth();
  const nomeEmpresa = estado?.conta?.nomeEmpresa ?? 'Oficina Ágil';
  const { valor: termo58mm } = useConfiguracao('termo_impressao_58mm');
  const [empresa, setEmpresa] = useState<DadosEmpresa | null>(null);

  const [caixa, setCaixa] = useState<CaixaResumo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  /**
   * Cupom em pré-visualização. É o mesmo modal para a venda recém-fechada
   * e para o "ver cupom" do histórico: mostrar o papel antes de imprimir
   * evita a folha errada e dispensa um passo extra de confirmação.
   */
  const [cupom, setCupom] = useState<VendaDetalhe | null>(null);
  /** true quando o cupom veio de uma venda que acabou de ser fechada. */
  const [recemFechada, setRecemFechada] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);
  /** Venda escolhida no histórico para devolver. */
  const [devolvendo, setDevolvendo] = useState<string | null>(null);
  const [avisoDevolucao, setAvisoDevolucao] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    void configuracoesApi
      .obterEmpresa()
      .then((d) => {
        if (!cancelado) setEmpresa(d);
      })
      .catch(() => undefined);
    return () => {
      cancelado = true;
    };
  }, []);

  // Retoma o caixa aberto desta sessão. Se o id guardado não existe mais
  // (fechado noutra máquina, ou fechado sozinho por ter passado da meia-
  // noite), volta para a abertura sem barulho.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const guardado = (() => {
          try {
            return localStorage.getItem(CHAVE_CAIXA);
          } catch {
            return null;
          }
        })();
        if (guardado) {
          const c = await pdvApi.obterCaixa(guardado).catch(() => null);
          if (vivo && c?.aberto) {
            setCaixa(c);
            return;
          }
        }
      } catch (e) {
        if (vivo) setErro(textoDoErro(e));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const guardarCaixa = useCallback((c: CaixaResumo | null) => {
    setCaixa(c);
    try {
      if (c) localStorage.setItem(CHAVE_CAIXA, c.id);
      else localStorage.removeItem(CHAVE_CAIXA);
    } catch {
      /* aba anônima ou storage bloqueado — a sessão continua funcionando */
    }
  }, []);

  /**
   * Manda o papel para a impressora. O cupom já está no DOM (o preview é
   * o que está na tela), então a medição da altura acontece sobre o que o
   * operador acabou de ver.
   */
  async function imprimirCupom() {
    setImprimindo(true);
    try {
      const pageSize = medirElementoComoPageSize('.preview-impressao--58mm .cupom-58', 58, 5);
      const r = await imprimirComClasse('imprimindo-cupom', { pageSize });
      if (!r.ok && !r.cancelado) {
        await dialogos.alertar(r.erro ?? 'Falha ao imprimir.');
      }
    } finally {
      setImprimindo(false);
    }
  }

  async function aoFinalizarVenda(vendaId: string) {
    try {
      const venda = await pdvApi.obterVenda(vendaId);
      // O modal de confirmação É a pré-visualização: o operador confere o
      // troco e o papel na mesma tela, e decide imprimir ali.
      setRecemFechada(true);
      setCupom(venda);
      const atualizado = await pdvApi.obterCaixa(caixa!.id);
      setCaixa(atualizado);
    } catch (e) {
      setErro(textoDoErro(e));
    }
  }

  if (carregando) {
    return <div className="pdv pdv--carregando">Carregando o PDV…</div>;
  }

  return (
    <div className="pdv">
      <header className="pdv__topo">
        <div className="pdv__identidade">
          <span className="pdv__marca">Venda rápida</span>
          {caixa && (
            <span className="pdv__caixa">
              Caixa de {caixa.colaboradorNome ?? '—'} · {caixa.quantidadeVendas} venda(s) ·{' '}
              {formatarDinheiro(caixa.totalVendas)}
            </span>
          )}
        </div>

        <nav className="pdv__abas" aria-label="Seções do PDV">
          <button
            type="button"
            className={`pdv__aba${aba === 'vender' ? ' pdv__aba--ativa' : ''}`}
            onClick={() => navegar(ROTAS.pdv)}
            disabled={!caixa}
          >
            Vender
          </button>
          <button
            type="button"
            className={`pdv__aba${aba === 'vendas' ? ' pdv__aba--ativa' : ''}`}
            onClick={() => navegar(ROTAS.pdvVendas)}
          >
            Vendas
          </button>
          <button
            type="button"
            className={`pdv__aba${aba === 'fiado' ? ' pdv__aba--ativa' : ''}`}
            onClick={() => navegar(ROTAS.pdvFiado)}
            disabled={!caixa}
          >
            Fiado
          </button>
          <button
            type="button"
            className={`pdv__aba${aba === 'devolucao' ? ' pdv__aba--ativa' : ''}`}
            onClick={() => {
              setDevolvendo(null);
              navegar(ROTAS.pdvDevolucao);
            }}
            disabled={!caixa}
          >
            Devolver
          </button>
          <button
            type="button"
            className={`pdv__aba${aba === 'caixa' ? ' pdv__aba--ativa' : ''}`}
            onClick={() => navegar(ROTAS.pdvCaixa)}
          >
            Caixa
          </button>
          <button
            type="button"
            className={`pdv__aba${aba === 'relatorio' ? ' pdv__aba--ativa' : ''}`}
            onClick={() => navegar(ROTAS.pdvRelatorio)}
          >
            Relatório
          </button>
        </nav>

        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => navegar(ROTAS.ordens)}
          aria-label="Sair do PDV"
        >
          <IconX className="btn__icon" />
          Sair do PDV
        </button>
      </header>

      {erro && (
        <p className="pdv-aviso pdv-aviso--erro" role="alert">
          {erro}
        </p>
      )}

      {avisoDevolucao && (
        <p className="pdv-aviso pdv-aviso--sucesso" role="status">
          {avisoDevolucao}
        </p>
      )}

      <div className="pdv__conteudo">
        {aba === 'relatorio' && <PdvRelatorio />}

        {aba === 'fiado' &&
          (caixa ? (
            <PdvFiado
              caixa={caixa}
              onRecebido={async ({ valor, naGaveta }) => {
                setAvisoDevolucao(
                  naGaveta
                    ? `${formatarDinheiro(valor)} recebidos e somados à gaveta do caixa.`
                    : `${formatarDinheiro(valor)} recebidos e baixados no financeiro.`,
                );
                setCaixa(await pdvApi.obterCaixa(caixa.id).catch(() => caixa));
              }}
            />
          ) : (
            <PdvAberturaCaixa onAberto={guardarCaixa} />
          ))}

        {aba === 'devolucao' &&
          (caixa ? (
            <PdvDevolucao
              caixa={caixa}
              vendaInicial={devolvendo}
              onVoltar={() => navegar(ROTAS.pdvVendas)}
              onConcluida={async ({ valor, numero }) => {
                setAvisoDevolucao(
                  `Devolução ${numero} registrada: ${formatarDinheiro(valor)} saíram do caixa.`,
                );
                setDevolvendo(null);
                setCaixa(await pdvApi.obterCaixa(caixa.id).catch(() => caixa));
                navegar(ROTAS.pdvVendas);
              }}
              onTrocar={(credito) => {
                // Troca é devolução seguida de venda: o operador cobra (ou
                // devolve) a diferença na tela de venda.
                setAvisoDevolucao(
                  `${formatarDinheiro(credito)} devolvidos. Monte a nova venda e cobre a diferença.`,
                );
                setDevolvendo(null);
                navegar(ROTAS.pdv);
              }}
            />
          ) : (
            <PdvAberturaCaixa onAberto={guardarCaixa} />
          ))}

        {aba === 'vendas' && (
          <PdvHistoricoVendas
            onImprimir={(v) => {
              setRecemFechada(false);
              setCupom(v);
            }}
            onDevolver={(id) => {
              setAvisoDevolucao(null);
              setDevolvendo(id);
              navegar(ROTAS.pdvDevolucao);
            }}
            podeDevolver={Boolean(caixa)}
          />
        )}

        {aba === 'caixa' &&
          (caixa ? (
            <PdvFechamentoCaixa
              caixa={caixa}
              onVoltar={() => navegar(ROTAS.pdv)}
              onFechado={() => {
                guardarCaixa(null);
                navegar(ROTAS.pdv);
              }}
            />
          ) : (
            <PdvAberturaCaixa
              onAberto={(c) => {
                guardarCaixa(c);
                navegar(ROTAS.pdv);
              }}
            />
          ))}

        {aba === 'vender' &&
          (caixa ? (
            <PdvVender caixa={caixa} onVendaFinalizada={(id) => void aoFinalizarVenda(id)} />
          ) : (
            <PdvAberturaCaixa onAberto={guardarCaixa} />
          ))}
      </div>

      {cupom && (
        <Modal
          aberto
          tamanho="md"
          titulo={
            recemFechada
              ? `Venda ${cupom.numero} finalizada`
              : `Cupom da venda ${cupom.numero}`
          }
          onFechar={() => setCupom(null)}
        >
          <div className="pdv-form-modal">
            {recemFechada && (
              <div className="pdv-cupom-resumo">
                <div>
                  <span className="pdv-total__rotulo">Total</span>
                  <strong>{formatarDinheiro(cupom.valorTotal)}</strong>
                </div>
                {trocoDaVenda(cupom) > 0 && (
                  <div className="pdv-cupom-resumo__troco">
                    <span className="pdv-total__rotulo">Troco</span>
                    <strong>{formatarDinheiro(trocoDaVenda(cupom))}</strong>
                  </div>
                )}
              </div>
            )}

            <div className="preview-impressao preview-impressao--58mm">
              <CupomVenda58mm
                venda={cupom}
                nomeEmpresa={nomeEmpresa}
                empresa={empresa}
                termo={termo58mm}
              />
            </div>

            <p className="pdv-cartao__ajuda">
              Papel de 58&nbsp;mm. A altura se ajusta ao conteúdo na hora de imprimir.
            </p>

            <div className="pdv-form-modal__acoes">
              <button type="button" className="btn btn--ghost" onClick={() => setCupom(null)}>
                {recemFechada ? 'Não imprimir' : 'Fechar'}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={imprimindo}
                onClick={() => void imprimirCupom()}
              >
                {imprimindo ? 'Enviando…' : 'Imprimir cupom'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* O que efetivamente vai para o papel: renderizado fora do #root
          para o @media print poder esconder o app inteiro. */}
      {cupom && (
        <PrintPortal>
          <CupomVenda58mm
            venda={cupom}
            nomeEmpresa={nomeEmpresa}
            empresa={empresa}
            termo={termo58mm}
          />
        </PrintPortal>
      )}

    </div>
  );
}
